import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    Divider,
    styled,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { isArray, isEmpty } from 'lodash';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrls, processApiPath } from '../../utils/url';
import { IconTooltip } from '../common/Tooltip/IconTooltip';
import { GET_LMS_AUTH } from '../../config/apiPaths';

export type LmsSyncDialogProps = DialogProps & {
    userUuid: string;
    lmsType: string;
    // either the ltiUuid or classId must be provided
    ltiUuid?: string;
    classId?: string;
};

export enum LmsSyncResult {
    Synced = 'synced',
    Skipped = 'skipped',
    Failed = 'failed',
    Unauthorized = 'unauthorized'
}

export interface LmsSyncStatus {
    success: boolean;
    result: LmsSyncResult;
}

interface LmsSyncResponse {
    status: LmsSyncStatus;
    messages: string[][] | string[];
    errors: string[][] | string[];
}

interface LmsSyncResponseError {
    error: string;
}

interface LmsSyncRequest {
    [key: string]: {
        url: string;
        title: string;
        hint?: string;
        inProgress: boolean;
        response?: LmsSyncResponse;
    };
}

const SectionBox = styled(Box)`
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    padding-bottom: 8px;
`;

const SectionHeader = styled(Box)`
    display: flex;
    align-items: center;
    height: 32px;
    flex-wrap: nowrap;
    flex-direction: row;
`;

const StatusContainer = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
`;

const StyledDialog = styled(Dialog)`
    & .MuiDialog-paper {
        min-width: 320px;
        max-width: 800px;
    }
` as typeof Dialog;

interface ResponseMessagesProps {
    response: LmsSyncResponse | LmsSyncResponseError;
    key: string;
}

const ResponseMessages = (props: ResponseMessagesProps) => {
    const { response, key } = props;
    const { t } = useTranslation('lms_sync');

    if (!response) {
        return (
            <Typography variant="body2" color="text.secondary" key={`lms-sync-${key}-no-content`}>
                {t('no-response')}
            </Typography>
        );
    }

    const errResponse = response as LmsSyncResponseError;
    if (errResponse.error) {
        return (
            <Typography variant="body2" color="error" key={`lms-sync-${key}-err-msg-simple`}>
                {t(errResponse.error)}
            </Typography>
        );
    }

    const succResponse = response as LmsSyncResponse;

    const mapMessages = (messages: string[] | string[][], type: string, color?: string) =>
        messages?.map((message: string | string[], index: number) => {
            const resourceKey = `lms-sync-${key}-${type}-${index}`;
            if (isArray(message)) {
                return (
                    <Typography variant="body2" key={resourceKey} color={color}>
                        {message.map((r) => t(r)).join('')}
                    </Typography>
                );
            }
            return (
                <Typography variant="body2" key={resourceKey} color={color}>
                    {t(message)}
                </Typography>
            );
        });

    const messages = mapMessages(succResponse!.messages, 'msg');
    const errors = mapMessages(succResponse!.errors, 'err-msg', 'error');

    return <>{[...errors, ...messages]}</>;
};

export const LmsSyncDialog = (props: LmsSyncDialogProps) => {
    const { ltiUuid, classId, lmsType, userUuid, onClose } = props;
    const { t } = useTranslation('lms_sync');
    const { api } = getUrls();

    const [requests, setRequests] = useState<LmsSyncRequest>({});

    const handleClose = useCallback(
        (event) => {
            onClose?.(event, 'backdropClick');
        },
        [onClose]
    );

    const syncRequests = useMemo(() => {
        const buildState = () => {
            const parts = [`user_uuid:${userUuid}`];

            if (ltiUuid) {
                parts.push(`lti_uuid:${ltiUuid}`);
            } else {
                parts.push(`class_id:${classId}`);
            }

            return parts.join('/');
        };

        const state = buildState();

        const buildUrl = (apiCallName: string) =>
            `${api}/v1/lti/${lmsType?.toLowerCase()}/${apiCallName}?state=${state}`;

        return {
            course: {
                url: buildUrl('lms_sync_course'),
                title: t('course-sync-title'),
                hint: t('course-sync-hint'),
                inProgress: false
            },
            roster: {
                url: buildUrl('lms_sync_roster'),
                title: t('roster-sync-title'),
                inProgress: false
            },
            contents: {
                url: buildUrl('lms_sync_contents'),
                title: t('content-sync-title'),
                inProgress: false
            }
        } as LmsSyncRequest;
    }, [api, classId, lmsType, ltiUuid, userUuid, t]);

    useEffect(() => {
        if (isEmpty(requests)) {
            setRequests(syncRequests);
        }
    }, [requests, syncRequests]);

    const triggerLmsAuth = () => {
        // give the user a chance to read the messages before the new tab opens
        setTimeout(
            () =>
                window.open(
                    processApiPath(GET_LMS_AUTH, { lmsType }, { state: `user_uuid:${userUuid}/lti_uuid:${ltiUuid}` }),
                    '_blank'
                ),
            3000
        );
    };

    const fetchData = async (url: string, key: string): Promise<LmsSyncResult> => {
        setRequests((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                inProgress: true
            }
        }));
        let response: LmsSyncResponse;
        try {
            const { data } = await axios.get<LmsSyncResponse>(url, {
                headers: getUserUuidHeaders(userUuid)
            });
            response = data;
        } catch (error: any) {
            if (error.response?.data?.status?.result) {
                response = error.response.data;
            } else {
                response = {
                    status: {
                        success: false,
                        result: LmsSyncResult.Failed
                    },
                    errors: [error.message],
                    messages: []
                };
            }
        }
        setRequests((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                inProgress: false,
                response
            }
        }));

        return response.status?.result;
    };

    const onClickLaunch = async () => {
        Object.keys(syncRequests).forEach((key) => {
            setRequests((prev) => {
                delete prev[key].response;
                return prev;
            });
        });

        // synchronously fetch course data - gathered info may be required for further requests
        const courseResult = await fetchData(syncRequests.course.url, 'course');
        if (courseResult === LmsSyncResult.Unauthorized) {
            triggerLmsAuth();
            return;
        }

        // asynchronously fetch the rest of the data
        const results = await Promise.all([
            fetchData(syncRequests.roster.url, 'roster'),
            fetchData(syncRequests.contents.url, 'contents')
        ]);
        if (results.find((result) => result === LmsSyncResult.Unauthorized)) {
            triggerLmsAuth();
        }
    };

    return (
        <StyledDialog open={props.open} onClose={handleClose}>
            <DialogTitle>{t('syncing-from-lms')}</DialogTitle>
            <DialogContent>
                {Object.keys(requests)?.map((key: string, index: number) => (
                    <SectionBox key={`request-content-${key}`}>
                        {index > 0 && <Divider key={`divider-${key}`} />}
                        <SectionHeader>
                            <Typography variant="body1">{syncRequests[key].title}</Typography>
                            {requests[key].hint && <IconTooltip>{syncRequests[key].hint}</IconTooltip>}
                        </SectionHeader>
                        {requests[key].inProgress ? (
                            <StatusContainer>
                                <CircularProgress size={20} />
                            </StatusContainer>
                        ) : (
                            <ResponseMessages response={requests[key].response!} key={key} />
                        )}
                    </SectionBox>
                ))}
            </DialogContent>
            <DialogActions>
                <Button size="medium" color="primary" variant="contained" onClick={onClickLaunch}>
                    {t('trigger-sync')}
                </Button>
                <Button size="medium" color="primary" variant="outlined" onClick={handleClose}>
                    {t('common:close')}
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};
